/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ChartItemData, ChartSeriesData, DataAxisProps, Group } from '../UNSAFE_visTypes/chart';
import { BarSeriesData } from '../../UNSAFE_BarChart/barChart.types';
import { getTime } from '../../PRIVATE_Axis/timeAxis';

/**
 * Returns the min and max value among the items rendered in the viewport.
 * @param series The array of chart series.
 * @param viewportMin The current viewport min.
 * @param viewportMax The current viewport max.
 * @returns
 */
export function getDataMinMax<K extends string | number, D extends ChartItemData<K>>(
  series: BarSeriesData<K, D>[],
  groups: Group[],
  xStartIndex: number,
  xEndIndex: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  isStacked: boolean,
  isLog: boolean,
  hiddenSet: Set<K>,
  hideAndShowBehavior: 'none' | 'withRescale' | 'withoutRescale'
) {
  let dataMin = Number.MAX_VALUE;
  let dataMax = -Number.MAX_VALUE;
  const idToDataMap = new Map<K, D>();
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
    let posCumulValue = 0;
    let negCumulValue = 0;

    series.forEach((_: BarSeriesData<K, D>, seriesIndex: number) => {
      const item = getDataItem(seriesIndex, groupIndex);
      if (!item || item.value == null) {
        return;
      }
      idToDataMap.set(item.id, item);
      if (groupIndex >= xStartIndex && groupIndex <= xEndIndex) {
        const skipData =
          item?.id != null && hiddenSet.has(item.id) && hideAndShowBehavior === 'withRescale';
        if (skipData || (item.value <= 0 && isLog)) {
          return;
        }
        if (isStacked) {
          posCumulValue += item.value > 0 ? item.value : 0;
          negCumulValue += item.value < 0 ? item.value : 0;
          dataMin = Math.min(isLog ? posCumulValue : negCumulValue, dataMin);
          dataMax = Math.max(posCumulValue, dataMax);
        } else {
          dataMin = Math.min(dataMin, item.value);
          dataMax = Math.max(dataMax, item.value);
        }
      }
    });
  }
  return { dataMin, dataMax, idToDataMap };
}

/**
 * Returns the dataMin and dataMax, used for axis extend calculations, from the chart data.
 * @param series
 * @param hiddenIds
 * @param xStartIndex
 * @param xEndIndex
 * @param yAxis
 * @returns
 */
export function getDataExtent<K extends string | number, D extends ChartItemData<K>>(
  series: BarSeriesData<K, D>[],
  groups: Group[],
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  xStartIndex: number,
  xEndIndex: number,
  isStacked: boolean,
  hiddenSet: Set<K>,
  hideAndShowBehavior: 'none' | 'withRescale' | 'withoutRescale',
  yAxis?: DataAxisProps
) {
  let dataMin = yAxis?.dataMin;
  let dataMax = yAxis?.dataMax;
  //TODO: For performance optimization we can return early if we donot need idToDataMap i.e when selectionMode is off.
  // if (dataMin != null && dataMax != null) {
  //   return { dataMin, dataMax };
  // }
  const data = getDataMinMax<K, D>(
    series,
    groups,
    xStartIndex,
    xEndIndex,
    getDataItem,
    isStacked,
    yAxis?.scale === 'log',
    hiddenSet,
    hideAndShowBehavior
  );

  if (dataMin == null) {
    dataMin = data.dataMin;
  }

  if (dataMax == null) {
    dataMax = data.dataMax;
  }
  return { dataMax, dataMin, idToDataMap: data.idToDataMap };
}

/**
 * For a given groupIndex gIndex, and seriesIndex sIndex, returns the next groupIndex that has not null data.
 * @param gIndex The groupIndex.
 * @param sIndex The seriesIndex.
 * @param groups The groups arrray.
 * @param getDataItem The function that returns data item for given seriesIndex and groupIndex.
 * @returns
 */
function findNextGroupWithData<D>(
  gIndex: number,
  sIndex: number,
  groups: Group[],
  getDataItem: (sIndex: number, gIndex: number) => D | undefined
) {
  let right = gIndex;
  while (right < groups.length) {
    const hasData = !!getDataItem(sIndex, right);
    if (hasData) {
      return right;
    }
    right += 1;
  }
  return -1;
}

/**
 * For a given groupIndex gIndex, and seriesIndex sIndex, returns the prev groupIndex that has not null data.
 * @param gIndex The groupIndex.
 * @param sIndex The seriesIndex.
 * @param groups The groups arrray.
 * @param getDataItem The function that returns data item for given seriesIndex and groupIndex.
 * @returns
 */
function findPreviousGroupWithData<D>(
  gIndex: number,
  sIndex: number,
  getDataItem: (sIndex: number, gIndex: number) => D | undefined
) {
  let left = gIndex;
  while (left >= 0) {
    const hasData = !!getDataItem(sIndex, left);
    if (hasData) {
      return left;
    }
    left -= 1;
  }
  return -1;
}

/**
 * Finds the nearest data group/item for the given x axis coordinate.
 * @param x The x coordinate.
 * @param groups The groups array.
 * @param series The series array.
 * @param getDataItem The function that returns data item for given seriesIndex and groupIndex.
 * @returns
 */
export function findNearestRegularAxisIndex<K, D>(
  x: number,
  groups: Group[],
  series: ChartSeriesData<K, D>[],
  getDataItem: (sIndex: number, gIndex: number) => D | undefined
) {
  const keyFunc = (index: number) => getTime(groups[index].id);

  if (groups.length < 2) {
    return 0;
  }

  if (keyFunc(0) > x) {
    return Math.min(
      ...series.map((_, sIndex) => findNextGroupWithData<D>(0, sIndex, groups, getDataItem))
    );
  }

  if (keyFunc(groups.length - 1) < x) {
    return Math.max(
      ...series.map((_, sIndex) =>
        findPreviousGroupWithData(groups.length - 1, sIndex, getDataItem)
      )
    );
  }

  const groupIndex = findGroup(0, groups.length - 1, x, keyFunc);

  const prevIndex = Math.max(
    ...series.map((_, sIndex) => findPreviousGroupWithData(groupIndex, sIndex, getDataItem))
  );

  if (prevIndex === groupIndex) return prevIndex;

  const nextIndex = Math.min(
    ...series.map((_, sIndex) => findNextGroupWithData<D>(groupIndex, sIndex, groups, getDataItem))
  );

  if (keyFunc(nextIndex) - x < x - keyFunc(prevIndex)) {
    return nextIndex;
  }
  return prevIndex;
}

/**
 * Finds the nearest data group/item for the given x axis coordinate in mixed frequency time axis chart.
 * @param x The x axis coordinate
 * @param groups The chart groups.
 * @param series The chart series.
 * @param getDataItem The function that returns data item for each series and groups.
 */
export function getNearestMixedFrequencyDataItem<K, D extends ChartItemData<K>>(
  target: number,
  series: ChartSeriesData<K, D>[],
  groups: Group[],
  getDataItem: (seriesIndex: number, groupIndex: number) => ChartItemData<K> | undefined
) {
  /**
   * first find the data items in each series closest to the given value and
   * then find the closest among them.
   * TODO: make this O(log(n))
   */
  return series.map((_, seriesIndex) => {
    let min = Number.MAX_VALUE;
    let minIndex = 0;
    for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
      const x = getDataItem(seriesIndex, groupIndex)?.x;
      if (!x) {
        continue;
      }
      const time = getTime(x);
      const dist = Math.abs(target - time);
      if (dist < min) {
        min = dist;
        minIndex = groupIndex;
      }
    }
    return { seriesIndex, groupIndex: minIndex };
  });
}

/**
 * For a given target value, binary searches the index between min and max. The keyFunc returns the
 * value for each given index.
 * @param min The minimum index to search.
 * @param max The maximum index to search.
 * @param target The target value to find the neareast index to.
 * @param keyFunc The function that returns the target value for each given index.
 * @returns
 */
function findGroup(min: number, max: number, target: number, keyFunc: (index: number) => number) {
  let left = min;
  let right = max;

  const foundIndex = (ll: number, rr: number) => {
    return rr - ll === 1 && keyFunc(ll) <= target && target <= keyFunc(rr);
  };

  let moveRight = 0;
  let step = Math.floor((right - left) / 2);
  while (!foundIndex(left, right)) {
    const newIndex = moveRight ? Math.floor(left + step) : Math.ceil(right - step);
    const diff = target - keyFunc(newIndex);
    if (0 <= diff) {
      left = newIndex;
      moveRight = moveRight ? 0 : 1;
    } else {
      right = newIndex;
    }
    step = Math.floor((right - left) / 2);
  }
  return target - keyFunc(left) < keyFunc(right) - target ? left : right;
}
